import { useSignalR } from '../components/providers/SignalRProvider.vue'
import type { SignalRReceiveEvent, SubscribeToEventOptions } from '../store'
import { ref } from 'vue'
import type {
  PromptAssetAddedSignalRPayload,
  PromptAssetUpdatedSignalRPayload,
  PromptAssetRemovedSignalRPayload
} from '@gat-ship/types/signal-r-events'
import { signalREvent } from '@gat-ship/types/signal-r-events'

export type AiEventHandlers = {
  onPromptAssetAdded?: (
    payload: SignalRReceiveEvent<PromptAssetAddedSignalRPayload>
  ) => void
  onPromptAssetUpdated?: (
    payload: SignalRReceiveEvent<PromptAssetUpdatedSignalRPayload>
  ) => void
  onPromptAssetRemoved?: (
    payload: SignalRReceiveEvent<PromptAssetRemovedSignalRPayload>
  ) => void
}

export function useAiSignalREvents(handlers?: AiEventHandlers) {
  const { subscribe } = useSignalR()

  const subscription = ref<() => void>()

  function subscribeToPromptAssetAdded(
    cb: (payload: SignalRReceiveEvent<PromptAssetAddedSignalRPayload>) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<PromptAssetAddedSignalRPayload>(
      signalREvent.Ai.PromptAssetAdded,
      cb,
      options
    )
  }

  function subscribeToPromptAssetUpdated(
    cb: (
      payload: SignalRReceiveEvent<PromptAssetUpdatedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<PromptAssetUpdatedSignalRPayload>(
      signalREvent.Ai.PromptAssetUpdated,
      cb,
      options
    )
  }

  function subscribeToPromptAssetRemoved(
    cb: (
      payload: SignalRReceiveEvent<PromptAssetRemovedSignalRPayload>
    ) => void,
    options?: SubscribeToEventOptions
  ) {
    return subscribe<PromptAssetRemovedSignalRPayload>(
      signalREvent.Ai.PromptAssetRemoved,
      cb,
      options
    )
  }

  function subscribeToPromptAssetEvents() {
    if (subscription.value) {
      return subscription.value
    }

    if (handlers) {
      const unsubscribeFunctions: (() => void)[] = []

      if (handlers.onPromptAssetAdded) {
        unsubscribeFunctions.push(
          subscribeToPromptAssetAdded(handlers.onPromptAssetAdded)
        )
      }
      if (handlers.onPromptAssetUpdated) {
        unsubscribeFunctions.push(
          subscribeToPromptAssetUpdated(handlers.onPromptAssetUpdated)
        )
      }
      if (handlers.onPromptAssetRemoved) {
        unsubscribeFunctions.push(
          subscribeToPromptAssetRemoved(handlers.onPromptAssetRemoved)
        )
      }
      subscription.value = () => {
        unsubscribeFunctions.forEach((unsubscribe) => unsubscribe())
      }
    }

    return subscription.value
  }

  function unsubscribeFromPromptAssetEvents() {
    subscription.value?.()
    subscription.value = undefined
  }

  return {
    subscribeToPromptAssetEvents,
    unsubscribeFromPromptAssetEvents
  }
}
